/* eslint-disable react/display-name */
import React, { useState, forwardRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import OvertimeIcon from '../../../../assets/scss/icon/sidenav/Overview.svg';
import { ListItem, Button, Collapse, colors } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref} style={{ flexGrow: 1 }}>
    <RouterLink {...props} />
  </div>
));

const makeIsActive = href => {
  const [hrefPath, hrefSearch] = href.split('?');
  const hrefParams = new URLSearchParams(hrefSearch || '');
  const hrefTab = hrefParams.get('tab'); // e.g. "villaDetails" or null

  return (match, location) => {
    if (!match) return false;
    const locationTab = new URLSearchParams(location.search).get('tab');

    if (hrefTab) {
      // "Manage Villa" — highlight only when tab=villaDetails
      return location.pathname === hrefPath && locationTab === hrefTab;
    }
    // "Business Profile" — highlight only when tab is NOT villaDetails (or no tab at all)
    return location.pathname === hrefPath && locationTab !== 'villaDetails';
  };
};

const useStyles = makeStyles(theme => ({
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0
  },
  button: {
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontSize: '14px',
    fontFamily: 'Poppins',
    '&:active, &:hover': {
      color: '#14279B'
    }
  },
  buttonLeaf: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    color: colors.blueGrey[800],
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  icon: {
    color: theme.palette.icon,
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    '&:active, &:hover': {
      color: '#14279B'
    }
  },
  expandIcon: {
    marginLeft: 'auto',
    height: 16,
    width: 16
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  active: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    borderRadius: '8px',
    borderLeft: '5px solid #0B1555',
    color: '#FFFFFF',
    fontWeight: theme.typography.fontWeightMedium,
    background: '#14279B',
    margin: '0 8px',
    width: 'calc(100% - 16px)',
    '& $icon': {
      color: '#FFFFFF'
    },
    '&:hover': {
      background: '#091b85'
    }
  },
  disabled_item: {
    opacity: 0.4,
    pointerEvents: 'none',
    cursor: 'not-allowed'
  }
}));

const NavigationListItem = props => {
  const {
    title,
    href,
    depth,
    children,
    icon: Icon,
    className,
    open: openProp,
    label: Label,
    isExpired,
    ...rest
  } = props;

  const classes = useStyles();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen(open => !open);
  };

  let paddingLeft = 12;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = {
    paddingLeft
  };

  if (children) {

    return (
      <ListItem
        {...rest}
        className={clsx(classes.item, className, isExpired && classes.disabled_item)}
        disableGutters>
        <Button
          act="true"
          className={classes.button}
          onClick={handleToggle}
          style={style}>
          {Icon && <Icon className={classes.icon} />}
          {title}
          {open ? (
            <ExpandLessIcon className={classes.expandIcon} color="inherit" />
          ) : (
            <ExpandMoreIcon className={classes.expandIcon} color="inherit" />
          )}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  } else {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.itemLeaf, className, isExpired && classes.disabled_item)}
        disableGutters>
        <Button
          activeClassName={classes.active}
          className={clsx(classes.buttonLeaf, `depth-${depth}`)}
          component={CustomRouterLink}
          exact
          isActive={makeIsActive(href)}
          style={style}
          to={href}>
          {Icon && <Icon className={classes.icon} />}
          {/* <img style={{ width: "100%" }}
            alt="Cookies"
            src={OvertimeIcon}
          /> */}
          {title}
          {Label && (
            <span className={classes.label}>
              <Label />
            </span>
          )}
        </Button>
      </ListItem>
    );
  }
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.any,
  label: PropTypes.any,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired
};

NavigationListItem.defaultProps = {
  depth: 0,
  open: false
};

export default NavigationListItem;
